import {addUser, UserBasicType, UsersUpdateType, useUpdateAccountUsers} from "@twigsby/client-data";
import {Controller, FieldValues, FormProvider, useForm} from "@twigsby/frontend-framework/lib/ReactHookForm";
import React, {useEffect, useState} from "react";
import {faUser} from "@fortawesome/pro-solid-svg-icons/faUser";
import {
    AlertBanner,
    ButtonSubmit, ColourSolid,
    FormWrapperLabel,
    Modal,
    ModalType,
    useNotifications, ValidationPatterns
} from "@twigsby/frontend-framework";
import {useTranslation} from "@twigsby/frontend-framework";
import {UniqueEmailControl} from "../../UniqueEmailControl";

interface AddUserModalType extends ModalType {
    actionedCallback?: any;
}

const CreateUserModal = (props: AddUserModalType) => {
    const { t } = useTranslation();
    const [emailControlActive, setEmailControlActive] = useState<boolean>(false);
    const [userSaving, setUserSaving] = useState<boolean>(false);

    const { addNotification } = useNotifications();

    const updateUsers = useUpdateAccountUsers();

    const formMethods = useForm({
        defaultValues: {
            name: '',
            emailAddress: ''
        }
    });

    const {
        control,
        setError,
        clearErrors,
        formState: { errors, isValid },
        watch
    } = formMethods;

    const HandleCreate = (data: FieldValues, e: any) => {
        setUserSaving(true);

        addUser(data as UserBasicType).then((r) => {
            updateUsers.mutate({
                updateType: UsersUpdateType.ADD,
                newData: r,
            });

            if(props.actionedCallback) {
                props.actionedCallback(r.userId);
            }

            addNotification({
                title: t('sends.users.create.results.userCreated'),
                icon: faUser,
                colour: ColourSolid.Success
            });
            props.closeCallback();
        }).catch((e) => {
            console.error(e.response.data);
        }).finally(() => setUserSaving(false))
    }

    useEffect(() => {
        formMethods.setFocus('name');
        return () => formMethods.reset();
    }, [props.isOpen]);

    return (
        <Modal title={t('sends.users.create.title')} isOpen={props.isOpen} closeCallback={() => props.closeCallback()}>

            <FormProvider {...formMethods}>
                <form onSubmit={formMethods.handleSubmit((data, e) => HandleCreate(data, e))}>
                    <fieldset className={'flex-column flex-gap-md'}>

                        <AlertBanner title={t('sends.users.create.messages.newUserEmail')} />

                        <Controller
                            control={control}
                            name="name"
                            rules={{ required: t('validation.required'), minLength: { value: 2, message: t('validation.tooShort')} }}
                            render={({ field, fieldState }) =>
                                <FormWrapperLabel
                                    label={t('sends.users.create.labels.name')}
                                    invalid={fieldState.invalid}
                                    validationMessage={fieldState.error?.message}
                                >
                                    <input
                                        type='text'
                                        defaultValue={field.value}
                                        aria-invalid={formMethods.formState.errors.name ? 'true' : 'false'}
                                        onChange={(e) => field.onChange(e.currentTarget.value)}
                                    />
                                </FormWrapperLabel>
                            }
                        />

                        <Controller
                            control={control}
                            name='emailAddress'
                            rules={{ required: t('validation.required'), pattern: { value: ValidationPatterns.email, message: 'E-mail format is incorrect' } }}
                            render={({ field, fieldState }) =>
                                <UniqueEmailControl
                                    name={'emailAddress'}
                                    label={t('sends.users.create.labels.email')}
                                    placeholder={t('sends.users.create.labels.email')}
                                    defaultValue={field.value}
                                    lookupActiveCallback={(state) => setEmailControlActive(state)}
                                    changeCallback={(e) => {
                                        field.onChange(e);
                                    }}
                                    validationCallback={(e, unique) => {
                                        if(unique) {
                                            clearErrors('emailAddress');
                                        } else {
                                            setError('emailAddress', {type: 'custom', message: e});
                                        }
                                    }}
                                    fieldState={fieldState}
                                />
                            }
                        />

                        <ButtonSubmit
                            loading={userSaving}
                            disabled={!isValid || emailControlActive}
                            title={t('sends.users.create.actions.add')}
                            fullWidth={true}
                            colour={ColourSolid.Primary}
                        />

                    </fieldset>
                </form>
            </FormProvider>
        </Modal>

    )
}

export default CreateUserModal
